"use client";

import { Link2, PodcastIcon as LucidePodcastIcon } from "lucide-react";
import React, { ReactNode } from "react";

import Link from "next/link";
import { SocialMediaDataTypesEnum } from "@punchup/data/legacy";
import { XIcon as XIconSVG } from "@/components/common/icons";
import { cn } from "@/utils/cn";

interface SocialMediaType {
  link: string;
  label: string;
  letter?: string;
  svgClassName?: string;
  className?: string;
}

interface SocialMediaParams extends Omit<SocialMediaType, "label"> {
  ariaLabel: string;
  linkAriaLabel: string;
  children: ReactNode;
  viewBox?: string;
}

function MediaIconLink({
  link,
  linkAriaLabel,
  ariaLabel,
  children,
  viewBox = "0 0 24 24",
  svgClassName,
  className,
}: SocialMediaParams) {
  return (
    <Link
      href={link}
      target="_blank"
      className={cn("p-1", className)}
      aria-label={linkAriaLabel}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        aria-label={ariaLabel}
        className={cn("h-6 w-6", svgClassName)}
        fill="currentColor"
        viewBox={viewBox}
        width={20}
        height={20}
      >
        {children}
      </svg>
    </Link>
  );
}

export function TikTokIcon({
  link,
  label,
  svgClassName,
  className,
}: SocialMediaType) {
  return (
    <MediaIconLink
      className={className}
      ariaLabel="TikTok logo"
      linkAriaLabel={`${label} on TikTok`}
      link={link}
      svgClassName={svgClassName}
      viewBox="0 0 448 512"
    >
      <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z" />
    </MediaIconLink>
  );
}
export function XIcon({
  link,
  label,
  svgClassName,
  className,
}: SocialMediaType) {
  return (
    <MediaIconLink
      className={className}
      ariaLabel="X/Twitter logo"
      linkAriaLabel={`${label} on X/Twitter`}
      link={link}
      svgClassName={svgClassName}
    >
      <XIconSVG />
    </MediaIconLink>
  );
}

export function FacebookIcon({
  link,
  label,
  svgClassName,
  className,
}: SocialMediaType) {
  return (
    <MediaIconLink
      className={className}
      ariaLabel="Facebook logo"
      linkAriaLabel={`${label} on Facebook`}
      link={link}
      svgClassName={svgClassName}
    >
      <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
    </MediaIconLink>
  );
}

export function InstagramIcon({
  link,
  label,
  svgClassName,
  className,
}: SocialMediaType) {
  return (
    <MediaIconLink
      className={className}
      ariaLabel="Instagram logo"
      linkAriaLabel={`${label} on Instagram`}
      link={link}
      svgClassName={svgClassName}
    >
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </MediaIconLink>
  );
}

export function YoutubeIcon({
  link,
  label,
  svgClassName,
  className,
}: SocialMediaType) {
  return (
    <MediaIconLink
      className={className}
      ariaLabel="YouTube logo"
      linkAriaLabel={`${label} on YouTube`}
      link={link}
      svgClassName={svgClassName}
    >
      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
    </MediaIconLink>
  );
}

export function LinkedInIcon({
  link,
  label,
  svgClassName,
  className,
}: SocialMediaType) {
  return (
    <MediaIconLink
      className={className}
      ariaLabel="LinkedIn logo"
      linkAriaLabel={`${label} on LinkedIn`}
      link={link}
      svgClassName={svgClassName}
      viewBox="0 0 256 256"
    >
      <path d="M218.123122,218.127392 L180.191928,218.127392 L180.191928,158.724263 C180.191928,144.559023 179.939053,126.323993 160.463756,126.323993 C140.707926,126.323993 137.685284,141.757585 137.685284,157.692986 L137.685284,218.123441 L99.7540894,218.123441 L99.7540894,95.9665207 L136.168036,95.9665207 L136.168036,112.660562 L136.677736,112.660562 C144.102746,99.9650027 157.908637,92.3824528 172.605689,92.9280076 C211.050535,92.9280076 218.138927,118.216023 218.138927,151.114151 L218.123122,218.127392 Z M56.9550587,79.2685282 C44.7981969,79.2707099 34.9413443,69.4171797 34.9391618,57.260052 C34.93698,45.1029244 44.7902948,35.2458562 56.9471566,35.2436736 C69.1040185,35.2414916 78.9608713,45.0950217 78.963054,57.2521493 C78.9641017,63.090208 76.6459976,68.6895714 72.5186979,72.8184433 C68.3913982,76.9473153 62.7929898,79.26748 56.9550587,79.2685282 M75.9206558,218.127392 L37.94995,218.127392 L37.94995,95.9665207 L75.9206558,95.9665207 L75.9206558,218.127392 Z M237.033403,0.0182577091 L18.8895249,0.0182577091 C8.57959469,-0.0980923971 0.124827038,8.16056231 -0.001,18.4706066 L-0.001,237.524091 C0.120519052,247.839103 8.57460631,256.105934 18.8895249,255.9977 L237.033403,255.9977 C247.368728,256.125818 255.855922,247.859464 255.999,237.524091 L255.999,18.4548016 C255.851624,8.12438979 247.363742,-0.133792868 237.033403,0.000790807055"></path>
    </MediaIconLink>
  );
}

export function PodcastIcon({
  link,
  label,
  svgClassName,
  className,
}: SocialMediaType) {
  return (
    <MediaIconLink
      className={className}
      ariaLabel="Podcast icon"
      linkAriaLabel={`${label}'s Podcast`}
      link={link}
      svgClassName={svgClassName}
      viewBox="0 0 24 24"
    >
      <LucidePodcastIcon className="w-6 h-6" />
    </MediaIconLink>
  );
}

export function CustomIcon({
  link,
  label,
  className,
  svgClassName,
}: SocialMediaType) {
  return (
    <MediaIconLink
      className={className}
      svgClassName={svgClassName}
      ariaLabel="Link icon"
      linkAriaLabel={`Custom link for ${label}`}
      link={link}
      viewBox="0 0 24 24"
    >
      <Link2 className="w-6 h-6" strokeWidth={2.5} />
    </MediaIconLink>
  );
}

export function SocialMediasRow({
  medias,
  label,
  className,
  linkClassName,
  iconClassName = "text-white text-opacity-75 hover:text-opacity-90 transition-colors duration-200 ease-in-out",
}: {
  medias: { type: SocialMediaDataTypesEnum; link: string; letter?: string }[];
  label: string;
  className?: string;
  linkClassName?: string;
  iconClassName?: string;
}) {
  return (
    <div className={cn("flex gap-x-1", className)}>
      {medias.map((media, i) => {
        return (
          <React.Fragment key={i}>
            {media.type === "Facebook" && (
              <FacebookIcon
                link={media.link}
                className={linkClassName}
                svgClassName={iconClassName}
                label={label}
              />
            )}
            {media.type === "Twitter" && (
              <XIcon
                link={media.link}
                className={linkClassName}
                svgClassName={iconClassName}
                label={label}
              />
            )}
            {media.type === "Instagram" && (
              <InstagramIcon
                link={media.link}
                className={linkClassName}
                svgClassName={iconClassName}
                label={label}
              />
            )}
            {media.type === "Youtube" && (
              <YoutubeIcon
                link={media.link}
                className={linkClassName}
                svgClassName={iconClassName}
                label={label}
              />
            )}
            {media.type === "TikTok" && (
              <TikTokIcon
                link={media.link}
                className={linkClassName}
                svgClassName={iconClassName}
                label={label}
              />
            )}
            {media.type === "LinkedIn" && (
              <LinkedInIcon
                link={media.link}
                className={linkClassName}
                svgClassName={iconClassName}
                label={label}
              />
            )}
            {media.type === "Podcast" && (
              <PodcastIcon
                link={media.link}
                className={linkClassName}
                svgClassName={iconClassName}
                label={label}
              />
            )}
            {media.type === "Custom" && (
              <CustomIcon
                link={media.link}
                className={linkClassName}
                svgClassName={iconClassName}
                label={label}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
